import * as feedActions from '../actions/feed.actions';
import * as objectActions from '../actions/object.actions';
import * as postActions from '../actions/post.actions';

import { Action, createReducer, on } from '@ngrx/store';
import {
  Feed,
  Filter,
  FilterItem,
  IncludeExclude,
  PeriodFilterItem,
} from '../models/core.models';
import { FilterItemResponse } from 'src/app/api/services/feed-api.service';
import {
  NomLanguages,
  SortDirection,
  feedDerivedTimeShown,
  feedSortFieldApplies,
} from '../constants';

import { augmentObjectWithIconAndImage } from 'src/app/shared/utils/object-utils';
import { produce } from 'immer';

export type AnyFilterItem = FilterItem | PeriodFilterItem;

export function isValueItem(item: AnyFilterItem): item is FilterItem {
  return 'raw_value' in item;
}

export function isPeriodItem(item: AnyFilterItem): item is PeriodFilterItem {
  return 'time_start' in item && 'time_end' in item;
}

export function isSameFilterItem(a: AnyFilterItem, b: AnyFilterItem): boolean {
  // Different shapes → not the same
  if (isValueItem(a) && isValueItem(b)) {
    return a.property_uri === b.property_uri && a.raw_value === b.raw_value;
  }

  if (isPeriodItem(a) && isPeriodItem(b)) {
    return (
      a.property_uri === b.property_uri &&
      a.time_start === b.time_start &&
      a.time_end === b.time_end
    );
  }

  return false;
}

export function findFilter(
  filters: Filter[],
  item: AnyFilterItem,
): Filter | undefined {
  for (const filter of filters) {
    if (filter.property_uri_facet !== item.property_uri) continue;

    const match = filter.items.find((i) => {
      return isSameFilterItem(i, item);
    });

    if (match) {
      return filter;
    }
  }

  return undefined;
}

// Unique key per item, used for Sets/maps
export function makeFilterKey(item: AnyFilterItem): string {
  if (isValueItem(item)) {
    return `${item.property_uri}::value::${item.raw_value}`;
  }

  if (isPeriodItem(item)) {
    return `${item.property_uri}::period::${item.time_start}-${item.time_end}`;
  }

  return `unknown`;
}

export interface FeedState {
  filters: Filter[];
  activeFilterItems: AnyFilterItem[];
  selectedFilterItems: AnyFilterItem[];
  widgetSearchQuery: string;
  feed: Feed;
  alertsCount: number;
  offset: number;
  page: number;
  itemsCount: number;
  selectedOnly: boolean;
  sortDir: SortDirection;
  sortField: string;
  timeShown: TimeShown;
  widgetsLanguageId: number;
  editMode: boolean;
  loadingAlgorithmPostIds: string[];
  appliedAlgorithmPostIds: string[];
}

export type TimeShown =
  | 'post_time_created_absolute'
  | 'post_time_created_relative'
  | 'post_time_published_absolute'
  | 'post_time_published_relative';

export const initialState: FeedState = {
  filters: [],
  activeFilterItems: [],
  selectedFilterItems: [],
  widgetSearchQuery: '',
  feed: null,
  alertsCount: 0,
  offset: 0,
  itemsCount: 30,
  page: 1,
  selectedOnly: false,
  sortDir: 'desc',
  sortField: 'post_time_published',
  timeShown: 'post_time_published_absolute',
  widgetsLanguageId: NomLanguages.ENGLISH, // preview language changable from Widgets Pane toolbar
  editMode: false,
  loadingAlgorithmPostIds: [],
  appliedAlgorithmPostIds: [],
};

function upsertFilterItem(
  list: AnyFilterItem[],
  item: AnyFilterItem,
  addIfMissing: boolean = true,
  replaceTitle: boolean = true,
): AnyFilterItem[] {
  const index = list.findIndex((i) => isSameFilterItem(i, item));

  item = { ...item, checked: true }; // make a copy
  if (index === -1) {
    if (addIfMissing) {
      return [...list, item];
    } else {
      return list;
    }
  }

  const newList = [...list];
  newList[index] = {
    ...newList[index],
    ...item,
    // A chart point under an attached pivot dimension may carry the dimension's
    // name as its value_name; an unresolved title must not replace a good one.
    title: replaceTitle ? item.title : newList[index].title || item.title,
  } as AnyFilterItem;
  return newList;
}

const _feedReducer = createReducer(
  initialState,

  on(feedActions.loadFeedFilters, (state) => {
    return { ...state, filters: [] };
  }),

  // ! Load Initial Filter Structure
  on(
    feedActions.loadFeedFiltersSuccess,
    produce((draft, { filters }) => {
      draft.filters = filters.map((filter) => ({
        ...filter,
        items: filter.items.map((item) =>
          mergeItemWithState(
            item,
            draft.activeFilterItems,
            draft.selectedFilterItems,
          ),
        ),
      }));
    }),
  ),

  on(feedActions.loadFeedSuccess, (state, { feed, setCurrentFeed }) => {
    if (!setCurrentFeed || !feed) {
      return state;
    }
    const timeShown = feedDerivedTimeShown(feed);

    return {
      ...state,
      feed: {
        ...augmentObjectWithIconAndImage(feed),
      },
      activeFilterItems: [],
      selectedFilterItems: [],

      sortDir: feed.sort_direction,
      // A persisted sort field the feed's type does not support (e.g. data
      // predating the applicability table, or a changed feed type) would
      // leave the Sort dropdown with no selection while still being sent to
      // the backend - fall back to the universal published-time sort.
      sortField:
        feed.order_field &&
        feedSortFieldApplies(feed.order_field, feed.object_type_id)
          ? feed.order_field
          : 'post_time_published',
      itemsCount: feed.state_params?.items_per_page,
      alertsCount: feed.count_alerts,
      timeShown: timeShown as TimeShown,
    };
  }),

  on(
    feedActions.toggleFilter,
    produce((draft, { filter }) => {
      const match = draft.filters.find(
        (f) => f.property_uri_facet === filter.property_uri_facet,
      );

      if (match) {
        match.expanded = !match.expanded;
      }
    }),
  ),

  on(
    feedActions.loadFilter,
    produce((draft, { filter }) => {
      const match = draft.filters.find(
        (f) => f.property_uri_facet === filter.property_uri_facet,
      );

      if (match) {
        match.loading = true;
      }
    }),
  ),

  on(
    feedActions.loadFilterFail,
    produce((draft, { filter }) => {
      const match = draft.filters.find(
        (f) => f.property_uri_facet === filter.property_uri_facet,
      );

      if (match) {
        match.loading = false;
      }
    }),
  ),

  on(feedActions.sortChanged, (state, { sortField }) => {
    return {
      ...state,
      sortField: sortField,
    };
  }),

  on(feedActions.sortDirectionChanged, (state, { sortDirection }) => {
    return {
      ...state,
      sortDir: sortDirection,
    };
  }),

  // ! loads Individual Filter Items - on Filter expand (for performance)
  on(
    feedActions.loadFilterSuccess,
    produce((draft, { filterResponse }) => {
      const match = draft.filters.find(
        (f) => f.property_uri_facet === filterResponse.property_uri,
      );

      if (!match) return;

      let mergedItems = mergeFilterItems(
        filterResponse,
        draft.activeFilterItems,
        draft.selectedFilterItems,
      );

      match.items =
        filterResponse.offset > 0
          ? [...match.items, ...mergedItems]
          : mergedItems;

      Object.assign(match, {
        offset: filterResponse.offset,
        has_more: filterResponse.has_more,
        loading: false,
      });
    }),
  ),

  on(
    feedActions.selectFilterItem,
    produce((draft, { propertyUri, filterItem, mode }) => {
      const filter = draft.filters.find(
        (f) => f.property_uri_facet === propertyUri,
      );

      if (filter) {
        // find the item in the filter (works for value + period items)
        const item = filter.items.find((i) => isSameFilterItem(i, filterItem));

        if (item) {
          item.checked = true;
          item.type = mode;
        }
      }

      // Already selected: update in place. Checked outside the facet lookup
      // above - a chart drill-down selects values whose facet was never
      // loaded, and every repeat click pushed a duplicate chip (WAP-1719).
      const alreadyPresent = draft.selectedFilterItems.find((i) =>
        isSameFilterItem(i, filterItem),
      );

      if (alreadyPresent) {
        alreadyPresent.type = mode;
        alreadyPresent.checked = true;
        return;
      }

      // We want only one period filter per property_uri,
      // so if one exists, replace/update it instead of pushing another
      if (isPeriodItem(filterItem)) {
        const existingIndex = draft.selectedFilterItems.findIndex(
          (i) => isPeriodItem(i) && i.property_uri === filterItem.property_uri,
        );

        if (existingIndex !== -1) {
          draft.selectedFilterItems[existingIndex] = {
            ...draft.selectedFilterItems[existingIndex],
            ...filterItem,
            checked: true,
            type: mode,
          };
          return;
        }
      }

      // if not found, add it as new selected item
      draft.selectedFilterItems.push({
        ...filterItem,
        checked: true,
        type: mode,
      });
    }),
  ),

  on(
    feedActions.selectFilterItemResolved,
    (state, { item, titleResolved = true }) => ({
      ...state,
      selectedFilterItems: upsertFilterItem(
        state.selectedFilterItems,
        item,
        true,
        titleResolved,
      ),
      activeFilterItems: upsertFilterItem(
        state.activeFilterItems,
        item,
        false,
        titleResolved,
      ),
    }),
  ),

  on(
    feedActions.deselectFilterItem,
    produce((draft, { propertyUri, filterItem, mode }) => {
      const filter = draft.filters.find(
        (f) => f.property_uri_facet === propertyUri,
      );

      if (filter) {
        // Works for value + period items
        const item = filter.items.find((i) => isSameFilterItem(i, filterItem));

        if (item) {
          item.checked = false;
          item.type = mode;
        }
      }

      // * Remove the deselected Filter Item from the selectedFilterItems array
      draft.selectedFilterItems = draft.selectedFilterItems.filter(
        (selectedFilter) => !isSameFilterItem(selectedFilter, filterItem),
      );
    }),
  ),

  // ! deactivate a Filter Item
  on(
    feedActions.deactivateFilterItem,
    produce((draft, { propertyUri, filterItem }) => {
      const filter = draft.filters.find(
        (f) => f.property_uri_facet === propertyUri,
      );

      const item = filter?.items.find((i) => isSameFilterItem(i, filterItem));

      if (item) {
        Object.assign(item, { checked: false, active: false });
      }

      // updated to pass the whole item, not raw_value
      removeFilterFromLists(draft, propertyUri, filterItem);

      draft.offset = 0;
      draft.page = 1;
    }),
  ),

  on(
    feedActions.updateActiveFilterItem,
    produce((draft, { propertyUri, filterItem, mode }) => {
      const filter = draft.filters.find(
        (f) => f.property_uri_facet === propertyUri,
      );

      const item = filter?.items.find((i) => isSameFilterItem(i, filterItem));

      if (item) {
        Object.assign(item, { checked: true, active: true, type: mode });
      }

      const updateList = (list: AnyFilterItem[]) => {
        const index = list.findIndex((i) => isSameFilterItem(i, filterItem));

        if (index !== -1) {
          // ensure it's active + selected with the new mode
          list[index].type = mode;
          list[index].checked = true;
          list[index].active = true;
        } else {
          list.push({
            ...filterItem,
            checked: true,
            active: true,
            type: mode,
          });
        }
      };

      updateList(draft.selectedFilterItems);
      updateList(draft.activeFilterItems);

      draft.offset = 0;
      draft.page = 1;
    }),
  ),

  on(
    feedActions.toggleFilterDimension,
    produce((draft, { dimension }) => {
      // * Check if the Dimension already exists in selectedFilterItems
      const existing = draft.selectedFilterItems.find(
        (item) => item.id === dimension.id,
      );

      if (existing) {
        // * Toggle its active flag
        existing.active = !existing.active;
      } else {
        // * Add a new Dimension filter with default values
        draft.selectedFilterItems.push({
          title: dimension.title,
          filter_title: 'Dimension Filter',
          kind: 'value',
          // name: '', // TODO: Find out -> do we need to add here name as empty string?
          property_uri: 'post_content_search',
          raw_value: dimension.fq ?? dimension.query,
          type: 'include' as IncludeExclude,
          id: dimension.id,
          checked: true,
          active: true,
        } as AnyFilterItem);
      }
    }),
  ),

  // ! activate Filters (apply selection)
  on(
    feedActions.activateFilters,
    produce((draft) => {
      // * copy Selected Items -> active
      draft.activeFilterItems = draft.selectedFilterItems.map((item) => ({
        ...item,
        active: true,
      }));

      // * update each Filter's Items
      for (const filter of draft.filters) {
        for (const item of filter.items) {
          item.active = draft.selectedFilterItems.some((sel) =>
            isSameFilterItem(sel, item),
          );
        }
      }

      draft.offset = 0;
      draft.page = 1;
    }),
  ),

  on(
    feedActions.clearFilters,
    produce((draft) => {
      // * Uncheck all items in all filters
      for (const filter of draft.filters) {
        for (const item of filter.items) {
          item.checked = false;
        }
      }

      // * Clear selected and active filters
      draft.activeFilterItems = [];
      draft.selectedFilterItems = [];
    }),
  ),

  on(feedActions.pageChanged, (state, { pageNumber }) => {
    return {
      ...state,
      offset: (pageNumber - 1) * state.itemsCount,
      page: pageNumber,
    };
  }),

  on(feedActions.itemsPerPageChanged, (state, { itemsPerPage }) => {
    return {
      ...state,
      itemsCount: itemsPerPage,
      offset: 0,
      page: 1,
    };
  }),

  // ! Both selection handlers are down to the counter: the `selected` flags live
  // ! in the list query, written by `FeedPostsService`. `count_selected_posts`
  // ! stays here because it is a **feed-wide** count seeded by the server - the
  // ! loaded page cannot tell you how many posts are selected across ten of them.
  on(
    postActions.togglePostSelect,
    produce((draft, { selected }) => {
      draft.feed.count_selected_posts += selected ? 1 : -1;
    }),
  ),

  on(
    feedActions.selectAllPosts,
    produce((draft, { delta }) => {
      draft.feed.count_selected_posts += delta;
    }),
  ),

  on(feedActions.feedUnloaded, (state) => {
    return initialState;
  }),

  on(
    feedActions.setDimensionJoinTypeSuccess,
    produce((draft, { dimensionId, joinTypeId, query }) => {
      const dimension = draft.feed.dimensions.find((d) => d.id === dimensionId);

      if (dimension) {
        dimension.join_type_id = joinTypeId;
      }

      draft.feed.query = query;
    }),
  ),

  on(
    feedActions.feedDetachDimensionSuccess,
    produce((draft, { dimensionId, query }) => {
      draft.feed.dimensions = draft.feed.dimensions.filter(
        (dimension) => dimension.id !== dimensionId,
      );
      draft.feed.query = query;
    }),
  ),

  on(
    feedActions.feedAttachDimensionsSuccess,
    produce((draft, { dimensions, query }) => {
      draft.feed.query = query;
      draft.feed.dimensions = dimensions;

      // * Clear filters since they're now replaced by the dimension
      for (const filter of draft.filters) {
        for (const item of filter.items) {
          item.checked = false;
        }
      }
      draft.activeFilterItems = [];
      draft.selectedFilterItems = [];
    }),
  ),

  on(feedActions.changeWidgetsLanaguage, (state, { languageId }) => {
    return {
      ...state,
      widgetsLanguageId: languageId,
    };
  }),

  // ! `widgetsLanguageId` is client state with a single writer,
  // ! `changeWidgetsLanaguage` above; `WidgetViewPresetService` derives it from
  // ! the `pv-widgets` view preset.

  on(feedActions.editFeedSuccess, (state, { feed }): FeedState => {
    if (state.feed?.id === feed.id) {
      // Replace the main feed
      return {
        ...state,
        editMode: false,
        feed: {
          ...augmentObjectWithIconAndImage(feed),
        },
      };
    }

    const dimensions = state.feed?.dimensions || [];
    const updatedDimensions = dimensions.map((dim: any) =>
      dim.id === feed.id
        ? {
            ...dim,
            ...augmentObjectWithIconAndImage(feed),
          }
        : dim,
    );

    // Check if any update happened
    const found = dimensions.some((dim: any) => dim.id === feed.id);
    if (!found) {
      return state; // No match, state unchanged
    }

    return {
      ...state,
      editMode: false,
      feed: {
        ...state.feed,
        dimensions: updatedDimensions,
      },
    };
  }),

  on(
    feedActions.clearFeedSelectedItems,
    produce((draft) => {
      // * Only the count - the flags themselves are cleared in the list query by
      // * `FeedPostsService.clearSelection`.
      draft.feed.count_selected_posts = 0;
    }),
  ),

  on(feedActions.searchPosts, (state, { query }) => {
    return {
      ...state,
      feed: {
        ...state.feed,
        view_properties: {
          ...state.feed.view_properties,
          quick_search_query: query,
        },
      },
      offset: 0,
      page: 1,
    };
  }),

  on(
    feedActions.toggleAdvancedSearch,
    produce((draft) => {
      draft.feed.view_properties.show_advanced_quick_search =
        !draft.feed.view_properties.show_advanced_quick_search;
    }),
  ),

  on(postActions.applyPostAlgorithm, (state, { postId }) => {
    return {
      ...state,
      loadingAlgorithmPostIds: [postId],
    };
  }),

  on(postActions.applySelectedPostsAlgorithm, (state, { postIds }) => {
    return {
      ...state,
      loadingAlgorithmPostIds: postIds,
    };
  }),

  // ! Spinner half only; the hook patches the posts.
  // ! `applySelectedPostsAlgorithmSuccess` carries the ids the patch found.
  on(
    postActions.applyPostAlgorithmSuccess,
    produce((draft, { posts }) => {
      for (const post of posts) {
        draft.loadingAlgorithmPostIds = [];
        draft.appliedAlgorithmPostIds.push(post.id);
      }
    }),
  ),

  on(
    postActions.applySelectedPostsAlgorithmSuccess,
    produce((draft, { postIds }) => {
      for (const postId of postIds) {
        draft.loadingAlgorithmPostIds = [];
        draft.appliedAlgorithmPostIds.push(postId);
      }
    }),
  ),

  on(feedActions.toggleFeedEditMode, (state) => {
    return {
      ...state,
      editMode: !state.editMode,
    };
  }),

  on(feedActions.setFeedEditMode, (state, { editMode }) => {
    return {
      ...state,
      editMode,
    };
  }),

  // ! The removed items themselves live in `injectFeedRemovedItemsQuery`. What
  // ! is left here is the selection reset the same call performs.
  on(
    feedActions.removeSelectedPostsFromFeedSuccess,
    produce((draft) => {
      draft.feed.count_selected_posts = 0;
    }),
  ),

  // ! The alerts themselves live in `injectFeedAlertsQuery`. Only the **count**
  // ! stays here: it is seeded from `feed.count_alerts` and has to be right
  // ! before the alerts panel is ever opened.
  on(feedActions.createAlertSuccess, (state) => ({
    ...state,
    alertsCount: state.alertsCount + 1,
  })),

  on(feedActions.deleteAlertSuccess, (state) => ({
    ...state,
    alertsCount: Math.max(0, state.alertsCount - 1),
  })),

  on(
    feedActions.toggleFeedView,
    produce((draft, { feedId, view, selected }) => {
      const views = draft.feed.views;
      const index = views.indexOf(view);

      if (selected) {
        if (index === -1) {
          views.push(view);
        }
      } else {
        if (index !== -1) {
          views.splice(index, 1);
        }
      }
    }),
  ),

  on(
    feedActions.setTimeShownOption,
    produce((draft, { option }) => {
      draft.timeShown = option;
    }),
  ),

  on(feedActions.setFeedPostViewModeSuccess, (state, { viewMode }) => {
    return {
      ...state,
      feed: {
        ...state.feed,
        view_posts_mode: viewMode,
      },
    };
  }),

  on(objectActions.updateObjectViewProperties, (state, { viewProperties }) => {
    if (!state.feed) return state; // no feed? skip updating

    return {
      ...state,
      feed: {
        ...state.feed,
        view_properties: {
          ...(state.feed.view_properties ?? {}),
          ...viewProperties,
        },
      },
    };
  }),

  on(
    feedActions.showSelectedPostsOnly,
    (state, { feedId, showSelectedOnly }) => {
      if (state.feed?.id !== feedId) {
        return state; // Only update if the feed matches
      }
      return {
        ...state,
        offset: 0, // reset offset
        selectedOnly: showSelectedOnly,
      };
    },
  ),
);

export function feedReducer(state: FeedState, action: Action) {
  return _feedReducer(state, action);
}

// ! merge a SINGLE Filter Item with existing active/selected state
export function mergeItemWithState(
  newItem: AnyFilterItem,
  activeItems: AnyFilterItem[],
  selectedItems: AnyFilterItem[],
): AnyFilterItem {
  const activeItem = activeItems.find((ai) => isSameFilterItem(ai, newItem));
  const selectedItem = selectedItems.find((si) =>
    isSameFilterItem(si, newItem),
  );

  return {
    ...newItem,
    // use ?? instead of || so `false` is preserved correctly
    checked: selectedItem?.checked ?? activeItem?.checked ?? false,
    active: activeItem?.active ?? false,
    type: activeItem?.type ?? selectedItem?.type ?? newItem.type ?? 'include',
  };
}

// ! merge ALL Filter Items from a filterResponse with existing active/selected state
export function mergeFilterItems(
  filterResponse: FilterItemResponse,
  activeItems: AnyFilterItem[],
  selectedItems: AnyFilterItem[],
): AnyFilterItem[] {
  // Merge server items with current active/selected state
  const mergedItems: AnyFilterItem[] = filterResponse.items.map((newItem) =>
    mergeItemWithState(newItem, activeItems, selectedItems),
  );

  // * preserve Items that exist locally but not in server response!
  const serverKeys = new Set(filterResponse.items.map((i) => makeFilterKey(i)));

  const result = [...mergedItems];

  // add missing active items for this property_uri
  activeItems.forEach((activeItem) => {
    if (
      activeItem.property_uri === filterResponse.property_uri &&
      !serverKeys.has(makeFilterKey(activeItem))
    ) {
      result.push({ ...activeItem });
    }
  });

  // add missing selected items for this property_uri
  selectedItems.forEach((selectedItem) => {
    if (
      selectedItem.property_uri === filterResponse.property_uri &&
      !serverKeys.has(makeFilterKey(selectedItem))
    ) {
      const alreadyAdded = result.some((m) =>
        isSameFilterItem(m, selectedItem),
      );
      if (!alreadyAdded) {
        result.push({ ...selectedItem });
      }
    }
  });

  return result;
}

// ! remove a Filter Item -> from both "selected" and "active" lists
function removeFilterFromLists(
  draft: FeedState,
  propertyUri: string,
  filterItem: AnyFilterItem,
) {
  draft.activeFilterItems = draft.activeFilterItems.filter(
    (item) => !isSameFilterItem(item, filterItem),
  );

  draft.selectedFilterItems = draft.selectedFilterItems.filter(
    (item) => !isSameFilterItem(item, filterItem),
  );
}
